import React, { useState, useEffect, useMemo, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import moment from "moment"
import CustomModal from "../basics/interface/CustomModal"
import { COLORS, modalStyles } from "../../utilities/Constants"
import { CATEGORIES, CATEGORY_ORDER } from "./agendaCategories"
import ClientAutocomplete from "./ClientAutocomplete"
import FilteredSelectV2 from "../basics/input/FilteredSelectV2"
import InputV2 from "../basics/input/InputV2"
import SelectV2 from "../basics/input/SelectV2"
import AppBtnDownloadPdf from "../basics/button/AppBtnDownloadPdf"
import { fetchEntiteVehicules } from "../../store/actions/agendaActions"
import {
	fetchAutreEntites,
	fetchMarques,
	fetchVehModeles,
} from "../../store/actions/autreActions"

// Création / édition d'un Rdv de l'agenda. Deux parties : infos de base (communes
// à tous les types) + section spécifique au type (ici dépannage). `onSave(payload)`
// persiste (POST api/rdvs), `onDelete(id)` supprime.

const DUREES = [
	{ value: 30, label: "30 min" },
	{ value: 60, label: "1 h" },
	{ value: 90, label: "1 h 30" },
	{ value: 120, label: "2 h" },
	{ value: 180, label: "3 h" },
]

// État initial du formulaire selon création (prefill) ou édition (rdv existant).
const initForm = (prefill, editing) => {
	if (editing) {
		const m = moment(editing.debut)
		const dep = editing.depannage || {}
		return {
			type: editing.type || "depannage",
			date: m.format("YYYY-MM-DD"),
			heure: m.format("HH:mm"),
			duree: editing.duree || 60,
			client: editing.client || editing.nom || "",
			clientId: editing.entite_id || null,
			telephone: editing.telephone || "",
			titre: editing.titre || "",
			commentaire: editing.commentaire || "",
			lieu: dep.lieu || "",
			destination: dep.destination || "",
			marque: dep.marque || "",
			modele: dep.modele || "",
			plaque: dep.plaque || "",
			tarif: dep.tarif != null ? String(dep.tarif) : "",
			encaisser_sur_place: !!dep.encaisser_sur_place,
			vehiculeId: dep.vehicule_id || null,
			assuranceId: dep.assurance_id || null,
		}
	}
	return {
		type: prefill.type || "depannage",
		date: prefill.date || moment().format("YYYY-MM-DD"),
		heure: prefill.heure || "08:00",
		duree: 60,
		// Préremplissage client (ex. ouverture depuis /clients) ; vide à l'agenda.
		client: prefill.client || "",
		clientId: prefill.clientId || null,
		telephone: prefill.telephone || "",
		titre: "",
		commentaire: "",
		lieu: "",
		destination: "",
		marque: "",
		modele: "",
		plaque: "",
		tarif: "",
		encaisser_sur_place: false,
		vehiculeId: null,
		assuranceId: null,
	}
}

const AgendaRdvModal = ({
	prefill = {},
	editing = null,
	onClose,
	onSave,
	onDelete,
	// Concrétisation Rdv dépannage → mission de dispatch (ouvre le formulaire
	// mission pré-rempli). Absent = bouton masqué.
	onConcretiser = null,
}) => {
	const dispatch = useDispatch()
	const [form, setForm] = useState(() => initForm(prefill, editing))
	// Véhicules de l'entité sélectionnée (pour le select véhicule).
	const [vehicules, setVehicules] = useState([])
	// Double-clic de confirmation sur le bouton Supprimer (convention modal V2).
	const [confirmDelete, setConfirmDelete] = useState(false)

	// État d'enregistrement/suppression (POST/DELETE api/rdvs) : pilote le grisage
	// + le spinner des boutons du footer pendant l'opération.
	const saving = useSelector((state) => state.agendaInfo.postRdv.saving)
	const deleting = useSelector((state) => state.agendaInfo.deleteRdv.deleting)
	const busy = saving || deleting

	// Entités assurance + assistance, fusionnées et triées alphabétiquement pour
	// le dropdown unique de couverture du dépannage.
	const assurances = useSelector((state) => state.autreInfo.assurances)
	const assistances = useSelector((state) => state.autreInfo.assistances)
	const couvertures = useMemo(
		() =>
			[...(assurances || []), ...(assistances || [])].sort((a, b) =>
				a.label.localeCompare(b.label, "fr")
			),
		[assurances, assistances]
	)

	// Catalogue marques/modèles (autosuggest véhicule). Les marques sont
	// préchargées au boot (NavV2) ; les modèles se chargent par marque.
	const marques = useSelector((state) => state.autreInfo.marques)
	const modeles = useSelector((state) => state.autreInfo.modeles)
	// Marque du catalogue sélectionnée (null = frappe libre) : conditionne la
	// liste de suggestions du champ Modèle.
	const [marqueId, setMarqueId] = useState(null)
	// Chaînage clavier : Enter passe au champ suivant (InputV2/SelectV2/
	// FilteredSelectV2/ClientAutocomplete partagent le même mécanisme nextRef).
	const heureRef = useRef(null)
	const dureeRef = useRef(null)
	const clientRef = useRef(null)
	const telephoneRef = useRef(null)
	const titreRef = useRef(null)
	const commentaireRef = useRef(null)
	const marqueRef = useRef(null)
	const modeleRef = useRef(null)
	const plaqueRef = useRef(null)
	const lieuRef = useRef(null)
	const destinationRef = useRef(null)
	const assuranceRef = useRef(null)
	const tarifRef = useRef(null)

	// Charge les listes absentes du store + raccroche la marque du Rdv édité au
	// catalogue (pour proposer ses modèles).
	useEffect(() => {
		if (!assurances || !assistances) dispatch(fetchAutreEntites())
		if (!marques || marques.length === 0) dispatch(fetchMarques())
		const m = (marques || []).find(
			(mq) =>
				mq.nom.toLowerCase() ===
				(form.marque || "").trim().toLowerCase()
		)
		if (m) {
			setMarqueId(m.id)
			dispatch(fetchVehModeles(m.id))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Frappe libre marque → texte conservé, mais plus de marque catalogue
	// rattachée (les suggestions de modèles se vident).
	const onMarqueChange = (texte) => {
		setForm((prev) => ({ ...prev, marque: texte }))
		if (marqueId) {
			setMarqueId(null)
			dispatch(fetchVehModeles(0))
		}
	}
	const onMarqueSelect = (marque) => {
		if (!marque) return
		setForm((prev) => ({ ...prev, marque: marque.nom }))
		setMarqueId(marque.id)
		dispatch(fetchVehModeles(marque.id))
	}
	const onModeleChange = (texte) =>
		setForm((prev) => ({ ...prev, modele: texte }))
	const onModeleSelect = (modele) => {
		if (!modele) return
		setForm((prev) => ({ ...prev, modele: modele.nom }))
	}

	const set = (key) => (e) => {
		const value = e.target.value
		setForm((prev) => ({ ...prev, [key]: value }))
	}

	// Charge les véhicules de l'entité quand une entité est rattachée (édition ou
	// sélection). Sans entité → liste vide (saisie manuelle).
	const chargerVehicules = (entiteId) => {
		if (!entiteId) {
			setVehicules([])
			return
		}
		dispatch(fetchEntiteVehicules(entiteId)).then((list) =>
			setVehicules(list || [])
		)
	}

	useEffect(() => {
		if (form.clientId) chargerVehicules(form.clientId)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Frappe libre dans le champ client → texte sans FK (entite_id = null).
	const onClientChange = (texte) => {
		setForm((prev) => ({ ...prev, client: texte, clientId: null }))
		setVehicules([])
	}

	// Sélection d'une entité → FK + téléphone auto + chargement de ses véhicules.
	const onClientSelect = (entite) => {
		setForm((prev) => ({
			...prev,
			client: entite.label,
			clientId: entite.id,
			telephone: prev.telephone || entite.telephone || "",
		}))
		chargerVehicules(entite.id)
	}

	// Sélection d'un véhicule existant (préremplit marque/modèle/plaque + FK) ou
	// « nouveau » (FK null, saisie libre).
	const onVehiculeSelect = (e) => {
		const val = e.target.value
		if (val === "new" || val === "") {
			// Retour à « nouveau » : on vide les champs véhicule (saisie libre).
			setForm((prev) => ({
				...prev,
				vehiculeId: null,
				marque: "",
				modele: "",
				plaque: "",
			}))
			setMarqueId(null)
			dispatch(fetchVehModeles(0))
			return
		}
		const veh = vehicules.find((v) => String(v.id) === val)
		if (!veh) return
		setForm((prev) => ({
			...prev,
			vehiculeId: veh.id,
			marque: veh.marque || "",
			modele: veh.modele || "",
			plaque: veh.plaques || "",
		}))
		// Raccroche la marque du véhicule au catalogue (suggestions de modèles
		// prêtes si l'utilisateur repasse en saisie libre).
		const m = (marques || []).find(
			(mq) =>
				mq.nom.toLowerCase() === (veh.marque || "").trim().toLowerCase()
		)
		setMarqueId(m ? m.id : null)
		dispatch(fetchVehModeles(m ? m.id : 0))
	}

	// Véhicule existant sélectionné → champs marque/modèle/plaque en lecture seule.
	const vehLocked = !!form.vehiculeId

	const isDepannage = form.type === "depannage"
	const valide = form.date && form.heure

	const submit = () => {
		const debut = moment(
			`${form.date} ${form.heure}`,
			"YYYY-MM-DD HH:mm"
		).format()
		const payload = {
			id: editing ? editing.id : null,
			type: form.type,
			statut: editing ? editing.statut : "planifie",
			debut,
			duree: Number(form.duree),
			nom: form.client.trim(),
			entite_id: form.clientId,
			telephone: form.telephone.trim(),
			titre: form.titre.trim(),
			commentaire: form.commentaire.trim(),
		}
		if (isDepannage) {
			payload.depannage = {
				lieu: form.lieu.trim(),
				destination: form.destination.trim(),
				marque: form.marque.trim(),
				modele: form.modele.trim(),
				plaque: form.plaque.trim(),
				tarif: Number(form.tarif) || 0,
				encaisser_sur_place: form.encaisser_sur_place,
				vehicule_id: form.vehiculeId,
				assurance_id: form.assuranceId,
			}
		}
		onSave(payload)
	}

	const handleDelete = () => {
		if (!confirmDelete) {
			setConfirmDelete(true)
			return
		}
		onDelete(editing.id)
	}

	// Rdv déjà concrétisé en mission : plus supprimable (trace), plus
	// re-concrétisable.
	const concretise = !!(editing && editing.statut === "concretise")
	const peutConcretiser = !!(
		onConcretiser &&
		editing &&
		editing.type === "depannage" &&
		!concretise &&
		editing.statut !== "annule"
	)

	const buttons = [
		...(editing && !concretise
			? [
					{
						title: confirmDelete ? "Confirmer ?" : "Supprimer",
						styleName: "danger",
						onClick: handleDelete,
						disabled: busy,
						loading: deleting,
					},
				]
			: []),
		{
			title: "Annuler",
			styleName: "secondary",
			onClick: onClose,
			disabled: busy,
		},
		{
			title: editing ? "Enregistrer" : "Créer le Rdv",
			styleName: "primary",
			onClick: submit,
			disabled: !valide || busy,
			loading: saving,
		},
	]

	return (
		<CustomModal
			title={editing ? "Modifier le rendez-vous" : "Nouveau rendez-vous"}
			subtitle="Planifier un Rdv dans l'agenda"
			onClose={onClose}
			buttons={buttons}
			footerLeft={
				editing && editing.type === "depannage" ? (
					<div style={{ display: "flex", gap: 8 }}>
						<AppBtnDownloadPdf
							apiRoute={`api/pdf?id=${editing.id}&type=depannage&pdf_type=rendez_vous`}
							btnTitle="Imprimer"
							style={modalStyles.btnSecondary}
						/>
						{peutConcretiser && (
							<button
								type="button"
								style={modalStyles.btnSecondary}
								onClick={() => onConcretiser(editing)}
								disabled={busy}
							>
								Concrétiser en mission
							</button>
						)}
						{concretise && (
							<span style={s.badgeConcretise}>
								✓ Concrétisé en mission
							</span>
						)}
					</div>
				) : null
			}
		>
			{/* Type (catégorie) */}
			<div style={s.field}>
				<span style={s.label}>Type</span>
				<div style={s.pills}>
					{CATEGORY_ORDER.map((key) => {
						const cat = CATEGORIES[key]
						const actif = form.type === key
						return (
							<button
								key={key}
								type="button"
								onClick={() =>
									setForm((prev) => ({ ...prev, type: key }))
								}
								style={{
									...s.pill,
									borderColor: actif ? cat.color : COLORS.border,
									backgroundColor: actif ? cat.bg : COLORS.surface,
									color: actif ? cat.color : COLORS.textMuted,
								}}
							>
								<span
									style={{
										...s.pastille,
										backgroundColor: cat.color,
									}}
								/>
								{cat.label}
							</button>
						)
					})}
				</div>
			</div>

			{/* --- Infos de base ------------------------------------------------ */}
			<div style={s.row3}>
				<div style={s.field}>
					<span style={s.label}>Date</span>
					<InputV2
						type="date"
						value={form.date}
						onChange={set("date")}
						nextRef={heureRef}
						style={s.input}
					/>
				</div>
				<div style={s.field}>
					<span style={s.label}>Heure</span>
					<InputV2
						ref={heureRef}
						type="time"
						value={form.heure}
						onChange={set("heure")}
						nextRef={dureeRef}
						style={s.input}
					/>
				</div>
				<div style={s.field}>
					<span style={s.label}>Durée</span>
					<SelectV2
						ref={dureeRef}
						value={form.duree}
						onChange={set("duree")}
						nextRef={clientRef}
						style={s.input}
					>
						{DUREES.map((d) => (
							<option key={d.value} value={d.value}>
								{d.label}
							</option>
						))}
					</SelectV2>
				</div>
			</div>

			<div style={s.row2}>
				<div style={s.field}>
					<span style={s.label}>Client</span>
					<ClientAutocomplete
						value={form.client}
						onChange={onClientChange}
						onSelect={onClientSelect}
						selected={!!form.clientId}
						placeholder="Nom / société"
						inputRef={clientRef}
						nextRef={telephoneRef}
					/>
				</div>
				<div style={s.field}>
					<span style={s.label}>Téléphone</span>
					<InputV2
						ref={telephoneRef}
						type="tel"
						value={form.telephone}
						onChange={set("telephone")}
						nextRef={titreRef}
						placeholder="079 …"
						style={s.input}
					/>
				</div>
			</div>

			<div style={s.field}>
				<span style={s.label}>Titre</span>
				<InputV2
					ref={titreRef}
					type="text"
					value={form.titre}
					onChange={set("titre")}
					nextRef={commentaireRef}
					placeholder="Libellé (optionnel)"
					style={s.input}
				/>
			</div>

			<div style={s.field}>
				<span style={s.label}>Commentaire</span>
				{/* Textarea volontairement hors chaînage sortant : Enter = retour
				    à la ligne. */}
				<textarea
					ref={commentaireRef}
					value={form.commentaire}
					onChange={set("commentaire")}
					rows={3}
					style={{ ...s.input, height: "auto", padding: "10px 12px" }}
				/>
			</div>

			{/* --- Section dépannage ------------------------------------------- */}
			{isDepannage && (
				<>
					<div style={s.sectionTitle}>Dépannage</div>

					{/* Véhicule : select des véhicules de l'entité (si rattachée) +
					    « nouveau », puis saisie marque/modèle/plaque. */}
					{form.clientId && vehicules.length > 0 && (
						<div style={s.field}>
							<span style={s.label}>Véhicule de l'entité</span>
							<SelectV2
								value={form.vehiculeId || "new"}
								onChange={onVehiculeSelect}
								// Véhicule existant → marque/modèle/plaque sont
								// verrouillés : Enter saute directement au Lieu.
								nextRef={vehLocked ? lieuRef : marqueRef}
								style={s.input}
							>
								<option value="new">Nouveau véhicule…</option>
								{vehicules.map((v) => (
									<option key={v.id} value={v.id}>
										{`${v.plaques} – ${v.marque} ${v.modele}`}
									</option>
								))}
							</SelectV2>
						</div>
					)}

					<div style={s.row3}>
						<div style={s.field}>
							<span style={s.label}>Marque</span>
							<FilteredSelectV2
								options={marques || []}
								defaultKey="nom"
								optionKeys="nom"
								value={form.marque}
								onChange={onMarqueChange}
								onSelect={onMarqueSelect}
								inputRef={marqueRef}
								nextRef={modeleRef}
								placeholder="Marque"
								disabled={vehLocked}
								inputStyle={{
									...s.input,
									...(vehLocked ? s.inputDisabled : {}),
								}}
							/>
						</div>
						<div style={s.field}>
							<span style={s.label}>Modèle</span>
							<FilteredSelectV2
								options={marqueId ? modeles || [] : []}
								defaultKey="nom"
								optionKeys="nom"
								value={form.modele}
								onChange={onModeleChange}
								onSelect={onModeleSelect}
								inputRef={modeleRef}
								nextRef={plaqueRef}
								placeholder="Modèle"
								disabled={vehLocked}
								inputStyle={{
									...s.input,
									...(vehLocked ? s.inputDisabled : {}),
								}}
							/>
						</div>
						<div style={s.field}>
							<span style={s.label}>Plaque</span>
							<InputV2
								ref={plaqueRef}
								type="text"
								value={form.plaque}
								onChange={set("plaque")}
								nextRef={lieuRef}
								disabled={vehLocked}
								style={{
									...s.input,
									...(vehLocked ? s.inputDisabled : {}),
								}}
							/>
						</div>
					</div>

					<div style={s.row2}>
						<div style={s.field}>
							<span style={s.label}>Lieu</span>
							<InputV2
								ref={lieuRef}
								type="text"
								value={form.lieu}
								onChange={set("lieu")}
								nextRef={destinationRef}
								style={s.input}
							/>
						</div>
						<div style={s.field}>
							<span style={s.label}>Destination</span>
							<InputV2
								ref={destinationRef}
								type="text"
								value={form.destination}
								onChange={set("destination")}
								nextRef={assuranceRef}
								style={s.input}
							/>
						</div>
					</div>

					<div style={s.field}>
						<span style={s.label}>Assurance / Assistance</span>
						<SelectV2
							ref={assuranceRef}
							value={form.assuranceId || ""}
							onChange={(e) => {
								const val = e.target.value
								setForm((prev) => ({
									...prev,
									assuranceId: val ? Number(val) : null,
								}))
							}}
							nextRef={tarifRef}
							style={s.input}
						>
							<option value="">—</option>
							{couvertures.map((c) => (
								<option key={c.id} value={c.id}>
									{c.label}
								</option>
							))}
						</SelectV2>
					</div>

					<div style={s.row2}>
						<div style={s.field}>
							<span style={s.label}>Tarif</span>
							<InputV2
								ref={tarifRef}
								type="number"
								value={form.tarif}
								onChange={set("tarif")}
								placeholder="0"
								style={s.input}
							/>
							{/* GE-66 K7 (passe 4, 🟡9) : la concrétisation d'un
							    privé ne reprend plus ce montant. */}
							{!form.assuranceId && (
								<span style={s.aideTarif}>
									Privé : tarif de la grille au terrain, ce
									montant n'est pas repris
								</span>
							)}
						</div>
						<div
							style={{
								...s.field,
								flexDirection: "row",
								alignItems: "center",
								gap: 8,
								marginTop: 22,
							}}
						>
							<input
								id="rdv-encsp"
								type="checkbox"
								checked={form.encaisser_sur_place}
								onChange={(e) => {
									// Lire `checked` AVANT l'updater différé (React 16
									// recycle l'event synthétique → e.target null sinon).
									const checked = e.target.checked
									setForm((prev) => ({
										...prev,
										encaisser_sur_place: checked,
									}))
								}}
							/>
							<label htmlFor="rdv-encsp" style={s.label}>
								Encaisser sur place
							</label>
						</div>
					</div>
				</>
			)}
		</CustomModal>
	)
}

const s = {
	field: { marginBottom: 14, display: "flex", flexDirection: "column" },
	aideTarif: { marginTop: 4, fontSize: 12, color: COLORS.textMuted },
	row2: {
		display: "grid",
		gridTemplateColumns: "1fr 1fr",
		gap: 14,
	},
	row3: {
		display: "grid",
		gridTemplateColumns: "1fr 1fr 1fr",
		gap: 14,
	},
	label: {
		fontSize: 12,
		fontWeight: 600,
		color: COLORS.textMuted,
		marginBottom: 6,
	},
	input: {
		width: "100%",
		height: 38,
		padding: "0 12px",
		borderRadius: 10,
		border: `1px solid ${COLORS.border}`,
		fontSize: 14,
		color: COLORS.text,
		fontFamily: "inherit",
		backgroundColor: COLORS.surface,
		boxSizing: "border-box",
	},
	inputDisabled: {
		backgroundColor: COLORS.bg,
		color: COLORS.textMuted,
		cursor: "not-allowed",
	},
	pills: { display: "flex", flexWrap: "wrap", gap: 8 },
	pill: {
		display: "inline-flex",
		alignItems: "center",
		gap: 8,
		height: 36,
		padding: "0 14px",
		borderRadius: 10,
		border: "1px solid",
		cursor: "pointer",
		fontSize: 13,
		fontWeight: 600,
		fontFamily: "inherit",
	},
	pastille: { width: 8, height: 8, borderRadius: 99, flexShrink: 0 },
	sectionTitle: {
		fontSize: 11,
		fontWeight: 700,
		letterSpacing: "0.08em",
		textTransform: "uppercase",
		color: COLORS.textLight,
		margin: "6px 0 12px",
		paddingTop: 12,
		borderTop: `1px solid ${COLORS.border}`,
	},
	badgeConcretise: {
		display: "inline-flex",
		alignItems: "center",
		padding: "0 10px",
		borderRadius: 8,
		fontSize: 12,
		fontWeight: 600,
		color: COLORS.success,
		backgroundColor: COLORS.successLight || "rgba(47, 158, 68, 0.12)",
	},
}

export default AgendaRdvModal
